import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@headlessui/react';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useGetTagsQuery } from 'containers/tags/api';
import { Tag } from 'containers/tags/tags.model';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import TagPill from 'containers/tags/create-tag/Tag';
import { CheckboxTickIcon, DropDownChevronDownIcon, ThinSearchIcon } from 'assets/icons';
import CreateTagModal from 'containers/tags/components/CreateTagModal';

interface TagSelectionSectionProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
}

const TagSelectionSection: FC<TagSelectionSectionProps> = ({ selectedTags, onTagsChange }) => {
  const currentJobSettingsPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { editJobSettings } = currentJobSettingsPermissions ?? {};
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [wasInputClicked, setWasInputClicked] = useState(false);
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);

  const currentTeam = useAppSelector(selectCurrentTeam);

  const {
    data: tagsData,
    isLoading,
    refetch
  } = useGetTagsQuery({
    teamId: currentTeam.id,
    isActive: true
  });

  const allTags = useMemo(() => {
    const tags = tagsData?.data?.tags || [];

    // Sort tags: selected tags first, then unselected
    return [...tags].sort((a, b) => {
      const aSelected = selectedTags.some(selectedTag => selectedTag.id === a.id);
      const bSelected = selectedTags.some(selectedTag => selectedTag.id === b.id);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [tagsData?.data?.tags]);

  // Focus input when popover opens after input was clicked
  useEffect(() => {
    if (wasInputClicked && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
        setWasInputClicked(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [wasInputClicked]);

  // Filter tags based on search query
  const filteredTags = useMemo(() => {
    return allTags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [allTags, searchQuery]);

  // Utility function to sort tags by priority
  const sortTagsByPriority = (tags: Tag[]) => {
    return [...tags].sort((a, b) => {
      const priorityA = a.priority ?? Infinity;
      const priorityB = b.priority ?? Infinity;
      return priorityA - priorityB;
    });
  };

  // Sort initial selected tags
  useEffect(() => {
    const sortedTags = sortTagsByPriority(selectedTags);
    if (JSON.stringify(sortedTags) !== JSON.stringify(selectedTags)) {
      onTagsChange(sortedTags);
    }
  }, []);

  const handleTagToggle = (tag: Tag) => {
    const isSelected = selectedTags.some(selectedTag => selectedTag.id === tag.id);

    if (isSelected) {
      const removedTags = selectedTags.filter(selectedTag => selectedTag.id !== tag.id);
      onTagsChange(sortTagsByPriority(removedTags));
    } else {
      const updatedTags = [...selectedTags, tag];
      onTagsChange(sortTagsByPriority(updatedTags));
    }
  };

  const handleRemoveTag = (tagId: string) => {
    onTagsChange(selectedTags.filter(tag => tag.id !== tagId));
  };

  const handleCreateTag = () => {
    refetch();
    setIsCreateTagModalOpen(false);
  };

  const handleOpenCreateTagModal = () => {
    setIsCreateTagModalOpen(true);
  };

  const renderDropdownContent = () => {
    if (isLoading) {
      return <div className="p-4 text-center text-sm text-accentText">Loading tags...</div>;
    }

    const createTagSection = editJobSettings && (
      <div
        className="mx-1 mb-2 cursor-pointer rounded-sm border-b border-borderGray p-2 text-sbase font-semibold
           text-primaryBtn hover:shadow-sm"
        onClick={handleOpenCreateTagModal}>
        + Create tag
      </div>
    );

    if (filteredTags.length === 0) {
      return (
        <>
          {createTagSection}
          <div className="p-4 text-center text-sm text-accentText">
            {searchQuery ? 'No tags found' : 'No tags available'}
          </div>
        </>
      );
    }

    const tagsList = filteredTags.map(tag => {
      const isSelected = selectedTags.some(selectedTag => selectedTag.id === tag.id);

      return (
        <div
          key={tag.id}
          className="flex cursor-pointer items-center justify-between border-b border-borderGray 
            py-[10px] last:border-b-0 hover:bg-highlightGray"
          onClick={() => handleTagToggle(tag)}>
          <div className="flex items-center gap-3">
            <TagPill tag={tag} className="px-2 py-1" textStyle="text-sm font-medium" />
          </div>
          <CustomCheckbox id={`tag-checkbox-${tag.id}`} isChecked={isSelected} onClick={() => handleTagToggle(tag)}>
            {isSelected && <CheckboxTickIcon className="h-3 w-3 text-white" />}
          </CustomCheckbox>
        </div>
      );
    });

    return (
      <>
        {createTagSection}
        {tagsList}
      </>
    );
  };

  return (
    <div className="w-full">
      <div className="pb-3 text-17px font-semibold text-heading">Tags</div>

      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="w-full">
              <div
                className="relative flex w-full items-center justify-between rounded-md border 
                border-borderGray bg-secondaryBtn p-3">
                <div className="relative flex grow items-start">
                  <div className="flex h-[26px] items-center justify-center">
                    <ThinSearchIcon />
                  </div>

                  <div className="flex flex-wrap gap-2.5 px-3">
                    {selectedTags.map(tag => (
                      <TagPill
                        key={tag.id}
                        tag={tag}
                        showCloseButton
                        handleRemoveTag={handleRemoveTag}
                        className="px-2 py-1"
                        textStyle="text-sm font-medium"
                      />
                    ))}

                    {/* Search Input */}
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search tags"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="min-w-0 flex-1 bg-transparent pt-1 text-17px text-primaryText 
                        outline-none placeholder:text-accentText"
                      onClick={e => {
                        // Only stop propagation if popover is already open
                        if (open) {
                          e.stopPropagation();
                        } else {
                          // Mark that input was clicked to focus it after popover opens
                          setWasInputClicked(true);
                        }
                      }}
                      onFocus={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                      onKeyDown={e => e.stopPropagation()}
                    />
                  </div>
                </div>
                <DropDownChevronDownIcon className="h-[8px] w-[14px] shrink-0 text-gray-400" />
              </div>
            </Popover.Button>

            <Popover.Panel
              className="absolute z-50 mt-1 w-full rounded-md border border-borderGray 
              bg-white shadow-lg">
              {/* Tags List */}
              <div className="max-h-60 overflow-y-auto px-3 py-4">{renderDropdownContent()}</div>
            </Popover.Panel>
          </>
        )}
      </Popover>

      <CreateTagModal
        isOpen={isCreateTagModalOpen}
        onClose={() => setIsCreateTagModalOpen(false)}
        onCreate={handleCreateTag}
      />
    </div>
  );
};

export default TagSelectionSection;
